import React, { useState, useEffect } from 'react';
import { Header, Modal, DataGrid } from '../layout';
import Swal from 'sweetalert2';
import { api } from '../services/api';
import { setDocumentTitle } from '../utils/titleUtils';
import {
    FaEye,
    FaCheckCircle,
    FaExclamationCircle,
    FaClock,
    FaUser,
    FaCalendarAlt,
    FaClipboardList,
    FaFilter,
    FaTimes,
    FaBoxOpen,
    FaEnvelope,
    FaFilePdf
} from 'react-icons/fa';

/* STATUS */
const STATUS_CONFIG = {
    inviata:   { label: 'Inviata',   bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: FaCheckCircle },
    errore:    { label: 'Errore',    bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-200',     dot: 'bg-red-500',     icon: FaExclamationCircle },
    in_attesa: { label: 'In Attesa', bg: 'bg-gray-100',   text: 'text-gray-600',    border: 'border-gray-200',    dot: 'bg-gray-400',    icon: FaClock },
};

const StatusPill = ({ statusKey }) => {
    const cfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.in_attesa;
    return (
        <span className={`inline-flex items-center gap-1.5 font-semibold rounded-full border text-xs px-2.5 py-1 ${cfg.bg} ${cfg.text} ${cfg.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
            {cfg.label}
        </span>
    );
};

const getStatusKey = (row) => {
    if (row.email_error) return 'errore';
    if (row.email_sent_date) return 'inviata';
    return 'in_attesa';
};

const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('it-IT');
};

/* MOBILE CARD */
const CertCard = ({ row, onView }) => {
    const sk = getStatusKey(row);
    const cfg = STATUS_CONFIG[sk];
    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`h-1 w-full ${cfg.dot}`} />
            <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                    <div>
                        <p className="font-mono font-bold text-gray-900 text-base leading-tight">{row.NUM_DOC}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(row.DATA_DOC)}</p>
                    </div>
                    <StatusPill statusKey={sk} />
                </div>
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <FaUser className="text-gray-400 text-[10px]" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{row.CLIENTE}</p>
                        <p className="text-xs text-gray-400 truncate font-mono">{row.ARTICOLO}</p>
                    </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div className="min-w-0 flex-1 mr-2">
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider">Inviata il</p>
                        <p className="text-xs font-semibold text-gray-700 truncate">{fmtDateTime(row.email_sent_date)}</p>
                    </div>
                    <button
                        onClick={() => onView(row)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold shadow-sm hover:bg-red-600 transition-colors"
                    >
                        <FaEye className="text-xs" />
                        Dettagli
                    </button>
                </div>
            </div>
        </div>
    );
};

/* STAT CARD */
const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
            <Icon className="text-base" />
        </div>
        <div className="min-w-0">
            <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
            <p className="text-[11px] text-gray-400 uppercase tracking-wider mt-0.5 truncate">{label}</p>
        </div>
    </div>
);

/* FILTER PANEL */
const FilterPanel = ({
    dateFrom, setDateFrom, dateTo, setDateTo,
    statusFilter, setStatusFilter, rows, isMobile, isOpen, onClose
}) => {
    const statusFilters = [
        { key: 'all', label: 'Tutti' },
        { key: 'inviata', label: 'Inviata' },
        { key: 'in_attesa', label: 'In Attesa' },
        { key: 'errore', label: 'Errore' },
    ];

    const getCount = (key) => key === 'all' ? rows.length : rows.filter(r => getStatusKey(r) === key).length;

    const inner = (
        <div className="flex flex-col gap-5">
            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaCalendarAlt /> Periodo
                </label>
                <div className="gap-2">
                    <div>
                        <p className="text-[10px] text-gray-400 mb-1">Dal</p>
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition" />
                    </div>
                    <div>
                        <p className="text-[10px] text-gray-400 mb-1">Al</p>
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-gray-50 focus:bg-white transition" />
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FaFilter /> Stato invio
                </label>
                <div className="flex flex-wrap gap-2">
                    {statusFilters.map(f => {
                        const active = statusFilter === f.key;
                        const cfg = STATUS_CONFIG[f.key];
                        const count = getCount(f.key);
                        return (
                            <button key={f.key}
                                onClick={() => { setStatusFilter(f.key); isMobile && onClose(); }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${active
                                    ? (cfg ? `${cfg.bg} ${cfg.text} ${cfg.border}` : 'bg-gray-200 text-gray-700 border-gray-300')
                                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                                {f.key !== 'all' && cfg && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />}
                                {f.label}
                                <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${active ? 'bg-white/60' : 'bg-gray-100'}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    if (!isMobile) {
        return (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-4">
                <p className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FaFilter className="text-red-400" /> Filtri
                </p>
                {inner}
            </div>
        );
    }

    return (
        <>
            <div className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} />
            <div className={`fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-3xl shadow-2xl transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}>
                <div className="flex justify-center pt-3 pb-1"><div className="w-10 h-1 rounded-full bg-gray-200" /></div>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <p className="font-bold text-gray-900 flex items-center gap-2"><FaFilter className="text-red-400" /> Filtri</p>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200">
                        <FaTimes className="text-sm" />
                    </button>
                </div>
                <div className="px-5 py-5 overflow-y-auto max-h-[75vh]">{inner}</div>
            </div>
        </>
    );
};

/* DESKTOP COLUMNS */
const toTime = (d) => (d ? new Date(d).getTime() : null);

const getCertColumns = (onView) => [
    {
        key: 'NUM_DOC',
        header: 'N. Doc',
        headerClassName: 'pl-6 pr-4',
        cellClassName: 'pl-6 pr-4',
        sortValue: (row) => row.NUM_DOC,
        render: (row) => <span className="font-mono font-bold text-gray-900 text-sm">{row.NUM_DOC}</span>
    },
    {
        key: 'DATA_DOC',
        header: 'Data',
        cellClassName: 'px-4 text-sm text-gray-500',
        sortValue: (row) => toTime(row.DATA_DOC),
        render: (row) => fmtDate(row.DATA_DOC)
    },
    {
        key: 'ARTICOLO',
        header: 'Articolo',
        cellClassName: 'px-4 font-mono text-xs text-gray-600',
        sortValue: (row) => row.ARTICOLO,
        render: (row) => row.ARTICOLO
    },
    {
        key: 'CLIENTE',
        header: 'Cliente',
        sortValue: (row) => row.CLIENTE,
        render: (row) => (
            <>
                <p className="text-sm font-semibold text-gray-800">{row.CLIENTE}</p>
                <p className="text-xs text-gray-400 truncate max-w-[280px]">{row.EMAIL}</p>
            </>
        )
    },
    {
        key: 'email_sent_date',
        header: 'Inviata il',
        cellClassName: 'px-4 text-xs text-gray-500 whitespace-nowrap',
        sortValue: (row) => toTime(row.email_sent_date),
        render: (row) => fmtDateTime(row.email_sent_date)
    },
    {
        key: 'status',
        header: 'Stato',
        sortValue: (row) => STATUS_CONFIG[getStatusKey(row)].label,
        render: (row) => <StatusPill statusKey={getStatusKey(row)} />
    },
    {
        key: 'actions',
        header: '',
        align: 'center',
        headerClassName: 'pl-4 pr-6',
        cellClassName: 'pl-4 pr-6',
        render: (row) => (
            <button onClick={() => onView(row)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-gray-200 text-gray-600 rounded-lg text-xs font-semibold hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-all shadow-sm">
                <FaEye /> Dettagli
            </button>
        )
    }
];

const CERT_SEARCH_KEYS = [
    'NUM_DOC',
    (row) => fmtDate(row.DATA_DOC),
    'ARTICOLO',
    'CLIENTE',
    'EMAIL',
    (row) => fmtDateTime(row.email_sent_date),
    (row) => STATUS_CONFIG[getStatusKey(row)].label
];
const CERT_INITIAL_SORT = { key: 'DATA_DOC', dir: 'desc' };
const getCertRowKey = (row) => row.id || `${row.NUM_DOC}-${row.DATA_DOC}-${row.ARTICOLO}-${row.CLIENTE}`;

/* DETAIL MODAL */
const CertDetailView = ({ row, onClose }) => {
    if (!row) return null;
    const sk = getStatusKey(row);
    const emails = (row.EMAIL || '').split(/[;,]/).map(e => e.trim()).filter(Boolean);

    return (
        <div className="-mx-5 -my-4 sm:-mx-6">
            <div className="px-5 sm:px-8 pt-5 pb-4 border-b border-gray-100">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-0.5">Certificazione</p>
                        <p className="text-xl font-bold text-gray-900 font-mono">{row.NUM_DOC}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(row.DATA_DOC)}</p>
                    </div>
                    <StatusPill statusKey={sk} />
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
                {[
                    { label: 'Tipo Doc', value: row.TIPO_DOC, mono: true },
                    { label: 'Articolo', value: row.ARTICOLO, mono: true },
                    { label: 'Cat. Omol.', value: row.CAT_OMO, mono: true },
                    { label: 'Tipo Cliente', value: row.TIPO_CL, mono: true },
                ].map((f, i) => (
                    <div key={i} className={`px-4 sm:px-6 py-3 ${i >= 2 ? 'border-t border-gray-100 sm:border-t-0' : ''}`}>
                        <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-0.5">{f.label}</p>
                        <p className={`text-sm font-semibold text-gray-900 truncate ${f.mono ? 'font-mono' : ''}`}>
                            {f.value || <span className="text-gray-300 font-normal">—</span>}
                        </p>
                    </div>
                ))}
            </div>

            <div className="px-5 sm:px-8 py-4 border-b border-gray-100">
                <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-1">
                    <FaUser className="inline mr-1" /> Cliente
                </p>
                <p className="text-sm font-semibold text-gray-900">{row.CLIENTE || '—'}</p>
            </div>

            {emails.length > 0 && (
                <div className="px-5 sm:px-8 py-4 border-b border-gray-100">
                    <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-2">
                        <FaEnvelope className="inline mr-1" /> Destinatari Cliente ({emails.length})
                    </p>
                    <p className="text-[10px] text-gray-400 mb-2 italic">Indirizzi email registrati del cliente</p>
                    <div className="flex flex-wrap gap-2">
                        {emails.map((e, i) => (
                            <span key={i} className="inline-flex items-center px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 font-mono">
                                {e}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {row.email_sent_to && (() => {
                const sentToList = row.email_sent_to.split(/[;,]/).map(e => e.trim()).filter(Boolean);
                const sameAsCustomer = sentToList.length === emails.length && sentToList.every(e => emails.includes(e));
                return (
                    <div className="px-5 sm:px-8 py-4 border-b border-gray-100 bg-emerald-50/40">
                        <p className="text-[9px] font-bold tracking-widest uppercase text-emerald-700 mb-2">
                            <FaEnvelope className="inline mr-1" /> Effettivamente Inviata a ({sentToList.length})
                        </p>
                        <p className="text-[10px] text-gray-500 mb-2 italic">
                            {sameAsCustomer
                                ? 'Coincide con i destinatari cliente'
                                : 'Differisce dai destinatari cliente registrati'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {sentToList.map((e, i) => {
                                const isExtra = !emails.includes(e);
                                return (
                                    <span key={i} className={`inline-flex items-center px-2.5 py-1 border rounded-lg text-xs font-mono ${isExtra ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white border-emerald-200 text-emerald-800'}`}>
                                        {e}
                                        {isExtra && <span className="ml-1.5 text-[9px] font-bold uppercase">extra</span>}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                );
            })()}

            <div className="px-5 sm:px-8 py-4 border-b border-gray-100">
                <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-2">Dettagli invio</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    <div>
                        <p className="text-gray-400">Data invio</p>
                        <p className="font-semibold text-gray-900">{fmtDateTime(row.email_sent_date)}</p>
                    </div>
                    <div>
                        <p className="text-gray-400">Creato il</p>
                        <p className="font-semibold text-gray-700">{fmtDateTime(row.created_at)}</p>
                    </div>
                    <div>
                        <p className="text-gray-400">Aggiornato il</p>
                        <p className="font-semibold text-gray-700">{fmtDateTime(row.updated_at)}</p>
                    </div>
                </div>
            </div>

            {row.pdf_attached && (
                <div className="px-5 sm:px-8 py-4 border-b border-gray-100 bg-indigo-50/50">
                    <p className="text-[9px] font-bold tracking-widest uppercase text-gray-400 mb-2">
                        <FaFilePdf className="inline mr-1" /> PDF Allegato
                    </p>
                    <p className="text-sm font-mono text-indigo-700">{row.pdf_attached}</p>
                </div>
            )}

            {row.email_error && (
                <div className="px-5 sm:px-8 py-4 border-b border-gray-100 bg-red-50/50">
                    <p className="text-[9px] font-bold tracking-widest uppercase text-red-500 mb-2">Errore</p>
                    <p className="text-xs text-red-700 whitespace-pre-wrap">{row.email_error}</p>
                </div>
            )}

            <div className="px-5 sm:px-8 py-4 flex justify-end">
                <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-700 underline underline-offset-2 transition-colors">
                    Chiudi
                </button>
            </div>
        </div>
    );
};

/* MAIN COMPONENT */
const InviiCertificazioni = () => {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentRow, setCurrentRow] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date(); d.setMonth(d.getMonth() - 1);
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

    const filteredRows = rows.filter(r => {
        if (statusFilter !== 'all' && getStatusKey(r) !== statusFilter) return false;
        if (r.DATA_DOC && (dateFrom || dateTo)) {
            const d = r.DATA_DOC.slice(0, 10);
            if (dateFrom && d < dateFrom) return false;
            if (dateTo && d > dateTo) return false;
        }
        return true;
    });

    useEffect(() => { setDocumentTitle('Invii Certificazioni'); }, []);
    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const res = await api.getCertificazioni();
            if (res.status === 'success') setRows(res.certificazioni || []);
            else throw new Error('Invalid response');
        } catch (e) {
            console.error(e);
            Swal.fire({ icon: 'error', title: 'Errore', text: 'Impossibile caricare le certificazioni' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = setStatusFilter;
    const handleDateFromChange = setDateFrom;
    const handleDateToChange = setDateTo;

    const handleView = (row) => { setCurrentRow(row); setModalOpen(true); };
    const closeModal = () => { setModalOpen(false); setCurrentRow(null); };

    if (isLoading && rows.length === 0) {
        return (
            <>
                <Header />
                <div className="flex flex-col justify-center items-center min-h-[60vh] gap-3">
                    <div className="w-10 h-10 border-[3px] border-gray-200 border-t-red-500 rounded-full animate-spin" />
                    <p className="text-sm text-gray-400">Caricamento certificazioni...</p>
                </div>
            </>
        );
    }

    const sentCount = rows.filter(r => r.email_sent_date && !r.email_error).length;
    const errorCount = rows.filter(r => r.email_error).length;
    const pendingCount = rows.filter(r => !r.email_sent_date && !r.email_error).length;

    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50/50">
                <div className="w-full px-3 sm:px-5 lg:px-8 py-6 sm:py-8">
                    <div className="mb-6 sm:mb-8">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg shadow-red-100 flex-shrink-0">
                                <FaClipboardList className="text-white text-lg sm:text-xl" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-gray-900 leading-tight">Invii Certificazioni</h1>
                                <p className="text-xs sm:text-sm text-gray-400 mt-0.5">Storico invii certificazioni (tabella dc)</p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                        <StatCard icon={FaClipboardList} label="Totale" value={rows.length} color="bg-red-100 text-red-600" />
                        <StatCard icon={FaCheckCircle} label="Inviate" value={sentCount} color="bg-emerald-100 text-emerald-600" />
                        <StatCard icon={FaClock} label="In attesa" value={pendingCount} color="bg-amber-100 text-amber-600" />
                        <StatCard icon={FaExclamationCircle} label="Errori" value={errorCount} color="bg-red-100 text-red-600" />
                    </div>

                    <div className="flex gap-6">
                        <div className="hidden lg:block w-72 flex-shrink-0">
                            <FilterPanel
                                dateFrom={dateFrom} setDateFrom={handleDateFromChange}
                                dateTo={dateTo} setDateTo={handleDateToChange}
                                statusFilter={statusFilter} setStatusFilter={handleFilterChange}
                                rows={rows} isMobile={false} isOpen={false} onClose={() => {}}
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-end gap-3 mb-4 lg:hidden">
                                <button onClick={() => setFilterDrawerOpen(true)}
                                    className="flex-shrink-0 h-10 px-4 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 flex items-center gap-2 shadow-sm hover:border-red-300 hover:text-red-600 transition-all">
                                    <FaFilter className="text-xs" />
                                    Filtri
                                    {statusFilter !== 'all' && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                </button>
                            </div>

                            <div className="lg:hidden space-y-3">
                                {filteredRows.length === 0 ? (
                                    <div className="bg-white rounded-2xl border border-gray-100 p-10 flex flex-col items-center gap-3 text-center">
                                        <FaBoxOpen className="text-3xl text-gray-300" />
                                        <p className="font-semibold text-gray-600">Nessuna certificazione trovata</p>
                                    </div>
                                ) : (
                                    filteredRows.map((r, i) => <CertCard key={r.id || `${r.NUM_DOC}-${r.DATA_DOC}-${i}`} row={r} onView={handleView} />)
                                )}
                            </div>

                            <div className="hidden lg:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                {filteredRows.length === 0 ? (
                                    <div className="py-16 flex flex-col items-center gap-3 text-center">
                                        <FaBoxOpen className="text-4xl text-gray-200" />
                                        <p className="font-semibold text-gray-500">Nessuna certificazione trovata</p>
                                    </div>
                                ) : (
                                    <DataGrid
                                        columns={getCertColumns(handleView)}
                                        rows={filteredRows}
                                        rowKey={getCertRowKey}
                                        searchKeys={CERT_SEARCH_KEYS}
                                        searchPlaceholder="Cerca documento, cliente, articolo..."
                                        initialSort={CERT_INITIAL_SORT}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FilterPanel
                dateFrom={dateFrom} setDateFrom={handleDateFromChange}
                dateTo={dateTo} setDateTo={handleDateToChange}
                statusFilter={statusFilter} setStatusFilter={handleFilterChange}
                rows={rows} isMobile={true} isOpen={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}
            />

            <Modal isOpen={modalOpen} onClose={closeModal} title="Dettagli Certificazione" size="90">
                <CertDetailView row={currentRow} onClose={closeModal} />
            </Modal>
        </>
    );
};

export default InviiCertificazioni;
